// The Offers workspace (docs/UI-POLISH.md §2 "Offers"; spec/dashboard.md section 7.3): the offers and offer groups in one
// list on the left, the selected one on the right in sections — where it runs (the game events that attach it, each with
// its condition and whether its time window is live right now), the art at a readable size, timing and limits, display,
// the store item and the groups that chain it, notes and tags. Every edit goes through the branch workspace (R-DB-01).
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useAssetUrls, useBranchAssetCtx } from '../../api/assetUrls';
import type { Document, Json, Parameter } from '../../api/types';
import { barsOf } from '../../content/calendar/bars';
import type { CardContext } from '../../content/cards/CardList';
import { ConditionSummary } from '../../content/conditions/ConditionBuilder';
import { useContent } from '../../content/ContentContext';
import { formatValue } from '../../content/format';
import { useDocumentActions } from '../../content/DocumentActions';
import { DocumentEditorHost } from '../../content/DocumentEditor';
import { useDocumentList } from '../../content/useDocumentList';
import { AssetDropField } from '../../content/widgets/AssetDropField';
import { createDocumentDraft, useEditorHost, useWidgetEnv } from '../../content/widgets/context';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { Button, IconButton } from '../../ui/Button';
import { Badge, draftTone, draftWord } from '../../ui/Chip';
import { CalendarDays, ChevronDown, Clock, Gift, Image as ImageIcon, Layers, Link as LinkIcon, MoreVertical, Plus, SlidersHorizontal, SquarePen, Store, Tag, Zap } from '../../ui/icons';
import { AttachEventDialog, OfferWizard, ScheduleDialog } from './OfferWizard';
import { TextInput } from '../../ui/inputs';
import { useUrlParam } from '../../ui/Modal';
import { Menu } from '../../ui/Popover';
import { Tabs } from '../../ui/Tabs';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useWorkspaceVersion } from '../../workspace/react';
import { entityKey, isTempId } from '../../workspace/Workspace';
import { BodyField, CommonTail, EVENT, GROUP, NameText, OFFER, StoreItemPreview, UsersBadge, paramsOf } from './cards';

type Kind = 'all' | 'offers' | 'groups';

function data(doc: Document): Record<string, Json> {
  return (doc.data ?? {}) as Record<string, Json>;
}

// the detail's sections in task order (docs/UX-REVIEW-2026-10-23.md section 2.3): Offer, Sells, When and where, Look, Limits, the template's own fields, Notes
const OFFER_FIELDS = ['name', 'description'];
const LOOK = ['unnyPlacement', 'unnyPriority'];
const TIMING = ['wait', 'duration', 'limit', 'cooldown', 'reactivate', 'lifetimeFrom'];
const GROUP_DISPLAY = ['type', 'duration', 'unnyPlacement', 'wait', 'unnyPriority'];
const HIDDEN = ['storeItem', 'storeItems', 'script'];

export function OffersWorkspace() {
  return (
    <DocumentEditorHost>
      <Workspace />
    </DocumentEditorHost>
  );
}

function Workspace() {
  const { org, game, can } = useGame();
  const { branch = '' } = useParams();
  const catalog = useContent();
  const { workspace } = useWidgetEnv();
  useWorkspaceVersion(workspace);
  const host = useEditorHost();
  const canWrite = can('content_write') && !workspace.isProtected;
  const tab = useUrlParam('tab');
  const kind: Kind = tab.value === 'groups' ? 'groups' : tab.value === 'offers' ? 'offers' : 'all';
  const selectedParam = useUrlParam('offer');
  const [filter, setFilter] = useState('');
  const [createAnchor, setCreateAnchor] = useState<HTMLElement | null>(null);
  const [wizard, setWizard] = useState(false);

  const offers = useDocumentList({ template: OFFER, includeHeirs: true, sort: { key: 'id', dir: 'desc' }, pageSize: 100 });
  const groups = useDocumentList({ template: GROUP, includeHeirs: true, sort: { key: 'id', dir: 'desc' }, pageSize: 100 });
  const events = useDocumentList({ template: EVENT, includeHeirs: true, sort: { key: 'id', dir: 'desc' }, pageSize: 200 });
  const groupIds = useMemo(() => new Set(groups.rows.map((g) => g.id)), [groups.rows]);
  const actions = useDocumentActions({ canWrite, serverOf: (doc) => (groupIds.has(doc.id) ? groups.serverOf(doc) : offers.serverOf(doc)), onReload: () => (offers.reload(), groups.reload()) });

  /** the events by the id of the offer or group they attach */
  const attachedBy = useMemo(() => {
    const map = new Map<string, Document[]>();
    for (const e of events.rows) {
      const target = data(e).script;
      if (typeof target !== 'string') continue;
      map.set(target, [...(map.get(target) ?? []), e]);
    }
    return map;
  }, [events.rows]);

  const labelOf = (doc: Document) => {
    const name = data(doc).name;
    return (typeof name === 'string' ? catalog.stringText(name) : undefined) ?? catalog.label(doc);
  };
  const f = filter.trim().toLowerCase();
  const rows = useMemo(() => {
    const all = kind === 'offers' ? offers.rows : kind === 'groups' ? groups.rows : [...offers.rows, ...groups.rows];
    return f ? all.filter((d) => labelOf(d).toLowerCase().includes(f) || (d.unnyId ?? '').toLowerCase().includes(f) || d.id.toLowerCase().includes(f)) : all;
  }, [kind, offers.rows, groups.rows, f]); // eslint-disable-line react-hooks/exhaustive-deps
  const selected = rows.find((d) => d.id === selectedParam.value) ?? rows[0] ?? null;
  const select = (id: string) => selectedParam.set({ offer: id });

  /** shows the new document: switches the kind filter to All when it would hide it */
  const show = (id: string, group: boolean) => {
    if (kind !== 'all' && group !== (kind === 'groups')) tab.set({ tab: null, offer: id });
    else select(id);
  };
  const create = (template: string) => {
    const def = catalog.templates.get(template);
    if (!def) return;
    const id = createDocumentDraft(workspace, def, template === GROUP ? { type: 1 } : {});
    setCreateAnchor(null);
    show(id, template === GROUP);
  };
  const loading = (offers.loading && offers.rows.length === 0) || (groups.loading && groups.rows.length === 0);
  const total = (offers.total ?? offers.rows.length) + (groups.total ?? groups.rows.length);

  return (
    <section className="offers-workspace">
      <div className="page-head">
        <h1>Offers</h1>
        <Badge>{offers.total ?? offers.rows.length} offers</Badge>
        <Badge>{groups.total ?? groups.rows.length} groups</Badge>
        <span className="spacer" />
        <Link className="btn btn-ghost btn-s" to={`${routes.documents(org, game.slug, branch, OFFER)}?heirs=1`}>
          Table view
        </Link>
        <Button variant="primary" size="s" onClick={() => setWizard(true)} missingPermission={can('content_write') ? null : 'content_write'} disabled={workspace.isProtected || !catalog.templates.has(OFFER)} icon={<Plus aria-hidden="true" />}>
          New offer…
        </Button>
        <Button variant="ghost" size="s" onClick={(e) => setCreateAnchor(e.currentTarget)} disabled={workspace.isProtected || !can('content_write')} aria-haspopup="menu" aria-label="More ways to create">
          More <ChevronDown aria-hidden="true" />
        </Button>
        <Menu
          anchor={createAnchor}
          open={createAnchor !== null}
          onClose={() => setCreateAnchor(null)}
          label="More ways to create"
          items={[
            { key: 'offer', label: 'Blank offer', icon: <Gift aria-hidden="true" />, onSelect: () => create(OFFER), disabled: !catalog.templates.has(OFFER) },
            { key: 'group', label: 'Offer group', icon: <Layers aria-hidden="true" />, onSelect: () => create(GROUP), disabled: !catalog.templates.has(GROUP) },
          ]}
        />
      </div>
      {wizard && <OfferWizard onClose={() => setWizard(false)} onCreated={({ offerId }) => show(offerId, false)} />}
      <div className="split offers-split">
        <aside className="pane list-pane offers-list" aria-label="Offers and groups">
          <div className="pane-head">
            <Tabs
              segmented
              ariaLabel="Kind"
              value={kind}
              onChange={(k) => tab.set({ tab: k === 'all' ? null : k })}
              items={[
                { key: 'all', label: 'All' },
                { key: 'offers', label: 'Offers' },
                { key: 'groups', label: 'Groups' },
              ]}
            />
            <TextInput size="s" aria-label="Filter offers" placeholder="Filter by name or id…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
          <div className="list-rows" role="list">
            {loading && (
              <div className="skeleton-rows" role="status">
                <span className="visually-hidden">Loading…</span>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="skeleton-row" aria-hidden="true">
                    <span className="skeleton" style={{ maxWidth: 32, height: 32 }} />
                    <span className="skeleton" />
                  </div>
                ))}
              </div>
            )}
            {rows.map((doc) => (
              <ListRow key={doc.id} doc={doc} group={groupIds.has(doc.id)} selected={selected?.id === doc.id} label={labelOf(doc)} events={attachedBy.get(doc.id) ?? []} onSelect={() => select(doc.id)} />
            ))}
            {!loading && rows.length === 0 && (
              <EmptyState icon={<Gift aria-hidden="true" />} title={f ? 'Nothing matches' : kind === 'groups' ? 'No offer groups yet' : 'No offers yet'} body={f ? 'Try another name or id.' : 'Create an offer with New offer…; it stages the event that shows it in the game too.'} />
            )}
            {(offers.hasMore || groups.hasMore) && !loading && (
              <div className="pane-foot">
                <Button size="s" onClick={() => (offers.hasMore ? offers.loadMore() : groups.loadMore())} busy={offers.loading || groups.loading}>
                  Load more
                </Button>
              </div>
            )}
          </div>
        </aside>
        <div className="offers-detail">
          {selected ? (
            <Detail key={selected.id} doc={selected} group={groupIds.has(selected.id)} events={attachedBy.get(selected.id) ?? []} eventsLoading={events.loading && events.rows.length === 0} actions={actions} canWrite={canWrite} open={() => void host?.open(selected.id)} onSelect={select} groupsOf={(storeItemId) => groups.rows.filter((g) => Array.isArray(data(g).storeItems) && (data(g).storeItems as Json[]).includes(storeItemId))} />
          ) : (
            !loading && (
              <div className="pane">
                <EmptyState icon={<Gift aria-hidden="true" />} title={total === 0 ? 'No offers on this branch' : 'Pick an offer'} body={total === 0 ? 'Create an offer with New offer…: what it sells, when and to whom it shows, how it looks.' : 'The list on the left opens an offer or a group here.'} />
              </div>
            )
          )}
        </div>
      </div>
      {actions.dialogs}
    </section>
  );
}

function Thumb({ doc, field, large }: { doc: Document; field: string; large?: boolean }) {
  const catalog = useContent();
  const id = data(doc)[field];
  const asset = typeof id === 'string' ? catalog.assets.get(id) : undefined;
  const src = useAssetUrls(useBranchAssetCtx(), asset?.contentPath ? [asset.id] : [])[asset?.id ?? ''];
  const cls = `thumb ${large ? 'thumb-l' : ''}`;
  return src ? <img className={cls} src={src} alt="" /> : <span className={`${cls} placeholder`} aria-hidden="true"><Gift /></span>;
}

function ListRow({ doc, group, selected, label, events, onSelect }: { doc: Document; group: boolean; selected: boolean; label: string; events: Document[]; onSelect: () => void }) {
  const { workspace } = useWidgetEnv();
  const draft = workspace.getDraft(entityKey('document', doc.id));
  const params = paramsOf(doc.template);
  const catalog = useContent();
  const d = data(doc);
  const placement = params.find((p) => p.name === 'unnyPlacement');
  const items = Array.isArray(d.storeItems) ? d.storeItems.length : 0;
  const sub = [group ? `${items} store item${items === 1 ? '' : 's'}` : null, placement && d.unnyPlacement != null ? formatPlacement(placement, d.unnyPlacement, catalog) : null, events.length === 0 ? 'no event' : `${events.length} event${events.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ');
  return (
    <button type="button" role="listitem" className={`list-row ${selected ? 'selected' : ''}`} aria-current={selected ? 'true' : undefined} onClick={onSelect} data-testid={`offer-row-${doc.id}`}>
      <Thumb doc={doc} field="icon" />
      <span className="text">
        <span className="title">{label}</span>
        <span className="sub">{sub}</span>
      </span>
      <span className="meta">
        {group && <Badge>group</Badge>}
        {draft && <Badge tone={draftTone[draft.status]}>{draftWord[draft.status]}</Badge>}
        {!doc.export && !draft && <Badge tone="warning">hidden</Badge>}
      </span>
    </button>
  );
}

function formatPlacement(param: Parameter, value: Json, catalog: ReturnType<typeof useContent>): string | null {
  if (param.type === 'enum' && param.enum) {
    const v = catalog.enums.get(param.enum)?.values.find((x) => String(x.value) === String(value));
    return v ? (v.displayName ?? v.name) : null;
  }
  return typeof value === 'string' ? value : null;
}

function Section({ icon, title, hint, children }: { icon: ReactNode; title: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <section className="detail-section">
      <h3>
        {icon}
        {title}
        {hint && <span className="muted small">{hint}</span>}
      </h3>
      {children}
    </section>
  );
}

const DAY = 86400;

/** Whether the time window of an event's condition is live right now (dev game time), and the next edge. */
function useLiveStatus(rootId: string | null) {
  const catalog = useContent();
  const { offsets } = useGame();
  const [zone] = useTimeZone();
  const now = Math.floor(Date.now() / 1000);
  const offset = offsets.dev ?? 0;
  const bars = barsOf(catalog, rootId, { start: now - DAY, end: now + 365 * DAY }, offset);
  const fmt = (t: number) => formatInstant(t, zone, { offsets });
  if (!bars.hasTime) return { cls: 'status-live', word: 'Always on', detail: 'no time window: on whenever the rest of the condition holds' };
  if (bars.never) return { cls: 'status-off', word: 'Never', detail: 'the time leaves can never hold together' };
  const current = bars.intervals.find((i) => i.start <= now && now < i.end);
  if (current) return { cls: 'status-live', word: 'Live now', detail: `until ${fmt(current.end)}` };
  const next = bars.intervals.find((i) => i.start > now);
  if (next) return { cls: 'status-scheduled', word: 'Scheduled', detail: `from ${fmt(next.start)}` };
  return { cls: 'status-ended', word: 'Ended', detail: 'no window ahead within a year' };
}

function EventRow({ event, onOpen }: { event: Document; onOpen: string }) {
  const catalog = useContent();
  const d = data(event);
  const status = useLiveStatus(typeof d.condition === 'string' ? d.condition : null);
  const params = paramsOf(event.template);
  const name = data(event).name;
  const label = (typeof name === 'string' ? catalog.stringText(name) : undefined) ?? catalog.label(event);
  const fact = (key: string) => {
    const p = params.find((x) => x.name === key);
    const v = d[key];
    if (!p || v == null) return null;
    const text = p.type === 'enum' ? formatPlacement(p, v, catalog) : String(v);
    return text ? (
      <span key={key} className="label-value">
        <span className="label">{p.displayName ?? p.name}</span>
        <span>{text}</span>
      </span>
    ) : null;
  };
  return (
    <div className="context-row" data-testid={`attached-by-${event.id}`}>
      <span className="context-status">
        <span className={`status-dot ${status.cls}`}>{status.word}</span>
        <span className="muted small">{status.detail}</span>
      </span>
      <Link className="event-name" to={onOpen} title="Open the event">
        {label}
      </Link>
      <span className="condition-text">
        <ConditionSummary rootId={typeof d.condition === 'string' ? d.condition : null} />
      </span>
      <span className="facts">
        {fact('unnyPlacement')}
        {fact('unnyPriority')}
      </span>
    </div>
  );
}

/** The chain of a group at a glance: each store item with its price and reward, in chain order. */
function ChainTable({ ids, onOpen }: { ids: Json[]; onOpen: (id: string) => string }) {
  const catalog = useContent();
  const params = catalog.templates.get('SmartObjects.StoreItem')?.effectiveParameters ?? [];
  const price = params.find((p) => p.name === 'price');
  const reward = params.find((p) => p.name === 'reward');
  const rows = ids.filter((id): id is string => typeof id === 'string');
  if (rows.length === 0) return null;
  return (
    <table className="chain-table" aria-label="Chain steps with price and reward">
      <thead>
        <tr>
          <th>Step</th>
          <th>Store item</th>
          <th>Price</th>
          <th>Reward</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((id, i) => {
          const item = catalog.resolve(id);
          const d = (item?.data ?? {}) as Record<string, Json>;
          return (
            <tr key={`${id}-${i}`}>
              <td className="num">{i + 1}</td>
              <td>{item ? <Link to={onOpen(id)}>{catalog.label(item)}</Link> : <span className="muted">unresolved {id.slice(0, 8)}</span>}</td>
              <td>{item && price ? formatValue(price, d.price, catalog.format) : '—'}</td>
              <td className="reward">{item && reward ? formatValue(reward, d.reward, catalog.format) : '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Detail({ doc, group, events, eventsLoading, actions, canWrite, open, onSelect, groupsOf }: { doc: Document; group: boolean; events: Document[]; eventsLoading: boolean; actions: ReturnType<typeof useDocumentActions>; canWrite: boolean; open: () => void; onSelect: (id: string) => void; groupsOf: (storeItemId: string) => Document[] }) {
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  const catalog = useContent();
  const { workspace } = useWidgetEnv();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const draft = workspace.getDraft(entityKey('document', doc.id));
  const ctx: CardContext = { doc, draft, expanded: true, canWrite: canWrite && draft?.status !== 'deleted', open, edit: (path, value) => actions.edit(doc, path, value) };
  const params = paramsOf(doc.template);
  const d = data(doc);
  const assetParams = params.filter((p) => p.type === 'asset');
  const known = new Set([...OFFER_FIELDS, ...LOOK, ...TIMING, ...GROUP_DISPLAY, ...HIDDEN, ...assetParams.map((p) => p.name)]);
  const more = params.filter((p) => !known.has(p.name));
  const [dialog, setDialog] = useState<null | 'schedule' | 'attach'>(null);
  const has = (name: string) => params.some((p) => p.name === name);
  const eventsPage = (eventId: string) => `${routes.branch(org, game.slug, branch, 'liveops/events')}?doc=${encodeURIComponent(eventId)}`;
  const storeItemId = typeof d.storeItem === 'string' ? d.storeItem : null;
  const chainedBy = storeItemId ? groupsOf(storeItemId) : [];
  const templateName = catalog.templates.get(doc.template)?.displayName ?? doc.template.split('.').pop();

  return (
    <article className="pane detail-pane" aria-label={`${group ? 'Offer group' : 'Offer'} ${doc.unnyId ?? doc.id}`} data-testid={`offer-detail-${doc.id}`}>
      <div className="detail-head">
        <Thumb doc={doc} field="icon" large />
        <div className="detail-title">
          <h2>
            <NameText doc={doc} />
            <Badge tone={group ? 'info' : 'accent'}>{group ? 'Group' : templateName}</Badge>
            {draft && <Badge tone={draftTone[draft.status]}>{draftWord[draft.status]}</Badge>}
            {!doc.export && <Badge tone="warning" title="Not exported: not part of releases">Hidden</Badge>}
          </h2>
          <div className="detail-ids">
            <UsersBadge kind={group ? 'offer_group' : 'offer'} id={doc.id} />
            <code>{doc.unnyId ?? (isTempId(doc.id) ? 'new' : doc.id.slice(0, 8))}</code>
            {!isTempId(doc.id) && <span>v{String(doc.version)}</span>}
            <code className="muted">{doc.template}</code>
          </div>
        </div>
        <div className="detail-actions">
          <Button size="s" variant="ghost" icon={<SquarePen aria-hidden="true" />} onClick={open}>
            Open editor
          </Button>
          <IconButton label={`Actions for ${doc.unnyId ?? doc.id.slice(0, 8)}`} onClick={(e) => setMenuAnchor(e.currentTarget)} aria-haspopup="menu">
            <MoreVertical aria-hidden="true" />
          </IconButton>
          <Menu anchor={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)} label="Document actions" items={actions.items(doc)} />
        </div>
      </div>
      <div className="detail-body">
        <Section icon={<Gift aria-hidden="true" />} title={group ? 'Offer group' : 'Offer'}>
          <div className="form-grid">
            {OFFER_FIELDS.filter(has).map((name) => (
              <BodyField key={name} ctx={ctx} params={params} name={name} />
            ))}
          </div>
        </Section>
        {group ? (
          <Section icon={<Store aria-hidden="true" />} title="Sells" hint="the store items, in chain order">
            <ChainTable ids={Array.isArray(d.storeItems) ? d.storeItems : []} onOpen={(id) => `${routes.documents(org, game.slug, branch, 'SmartObjects.StoreItem')}?doc=${encodeURIComponent(id)}`} />
            <BodyField ctx={ctx} params={params} name="storeItems" help="The order is the chain order" />
          </Section>
        ) : (
          has('storeItem') && (
            <Section icon={<Store aria-hidden="true" />} title="Sells" hint="the store item, its price and reward">
              <div className="store-item-row">
                <div className="store-item-field">
                  <BodyField ctx={ctx} params={params} name="storeItem" />
                </div>
                <StoreItemPreview id={d.storeItem} />
              </div>
              {storeItemId && (
                <div className="facts chained">
                  <span className="label-value">
                    <span className="label">In groups</span>
                    {chainedBy.length === 0 ? (
                      <span className="muted">none</span>
                    ) : (
                      chainedBy.map((g) => (
                        <button key={g.id} type="button" className="link" onClick={() => onSelect(g.id)}>
                          {catalog.label(g)}
                        </button>
                      ))
                    )}
                  </span>
                  <Link className="link" to={`${routes.documents(org, game.slug, branch, 'SmartObjects.StoreItem')}?doc=${encodeURIComponent(storeItemId)}`}>
                    Open the store item
                  </Link>
                </div>
              )}
            </Section>
          )
        )}
        <Section icon={<Zap aria-hidden="true" />} title="When and where" hint={events.length ? `${events.length} game event${events.length === 1 ? '' : 's'} attach${events.length === 1 ? 'es' : ''} it` : undefined}>
          {events.map((e) => (
            <EventRow key={e.id} event={e} onOpen={eventsPage(e.id)} />
          ))}
          {events.length === 0 && (
            <div className="context-empty" data-testid="not-attached">
              {eventsLoading ? (
                <span className="loading-line">
                  <span className="spinner" aria-hidden="true" /> Reading the game events…
                </span>
              ) : (
                <>
                  <span className={`status-dot status-off`}>Not attached</span>
                  <span className="muted small">No game event shows this {group ? 'group' : 'offer'} yet: schedule it, or attach it to an event that exists.</span>
                  {canWrite ? (
                    <>
                      <Button size="s" variant="primary" icon={<CalendarDays aria-hidden="true" />} onClick={() => setDialog('schedule')}>
                        Schedule this {group ? 'group' : 'offer'}…
                      </Button>
                      <Button size="s" icon={<LinkIcon aria-hidden="true" />} onClick={() => setDialog('attach')}>
                        Attach to an existing event…
                      </Button>
                    </>
                  ) : (
                    <Link className="btn btn-s" to={routes.branch(org, game.slug, branch, 'liveops/events')}>
                      Game events
                    </Link>
                  )}
                </>
              )}
            </div>
          )}
        </Section>
        <Section icon={<ImageIcon aria-hidden="true" />} title="Look">
          {assetParams.length > 0 && (
            <div className="art-grid">
              {assetParams.map((p) => (
                <figure key={p.name} className="art-figure">
                  <figcaption className="field-label">{p.displayName ?? p.name}</figcaption>
                  <AssetDropField param={p} value={d[p.name]} disabled={!ctx.canWrite} owner={doc.id} onCommit={(v) => ctx.edit(`data.${p.name}`, v)} />
                </figure>
              ))}
            </div>
          )}
          <div className="form-grid">
            {(group ? GROUP_DISPLAY : LOOK).filter(has).map((name) => (
              <BodyField key={name} ctx={ctx} params={params} name={name} />
            ))}
          </div>
        </Section>
        {!group && TIMING.some(has) && (
          <Section icon={<Clock aria-hidden="true" />} title="Limits">
            <div className="form-grid">
              {TIMING.filter(has).map((name) => (
                <BodyField key={name} ctx={ctx} params={params} name={name} />
              ))}
            </div>
          </Section>
        )}
        {more.length > 0 && (
          <Section icon={<SlidersHorizontal aria-hidden="true" />} title="Template fields" hint={`${more.length} of ${templateName}`}>
            <div className="form-grid">
              {more.map((p) => (
                <BodyField key={p.name} ctx={ctx} params={params} name={p.name} />
              ))}
            </div>
          </Section>
        )}
        <Section icon={<Tag aria-hidden="true" />} title="Notes and tags">
          <div className="form-grid">
            <CommonTail ctx={ctx} />
          </div>
        </Section>
      </div>
      {dialog === 'schedule' && <ScheduleDialog offer={doc} onClose={() => setDialog(null)} />}
      {dialog === 'attach' && <AttachEventDialog offer={doc} onClose={() => setDialog(null)} />}
    </article>
  );
}

